import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import * as Y from 'yjs'
import type { Awareness } from 'y-protocols/awareness'
import { useLang } from '../../i18n'
import { GridCell } from './GridCell'
import { appendHistory } from '../editor/history-log'
import { useIdle } from '../editor/useIdle'
import type { CellChange, HistoryEntry, Revision } from '../../lib/types'
import {
  GRID_CELL_TYPES,
  cellAt,
  coerceCell,
  deleteColumn,
  deleteRow,
  formatCell,
  insertColumn,
  insertRow,
  moveRow,
  readGrid,
  rowLabel,
  updateColumn,
  writeCell,
  type GridCellType,
  type GridSnapshot,
} from '../../lib/grid'
import './grid.css'

interface GridEditorProps {
  ydoc: Y.Doc
  awareness: Awareness | null
  /** Display name of this member, written onto the history entries they produce. */
  actor: string
  readOnly?: boolean
  /** The room's live claims. A cell claim is ADVISORY — it marks cells someone is working on; it
   *  does not block typing, because Yjs already resolves two writes to one cell deterministically
   *  (backend/domain/locks.ts#blocks). An UNSCOPED claim is a structural op in flight and DOES
   *  freeze the grid, because a concurrent deleteRow is the one case a grid can lose data. */
  revisions?: Revision[]
  /** Bumped by the lock notice's Release (AB#301): leave the focused cell, i.e. drop this member's lock. */
  releaseSignal?: number
}

/** Where the roving tabindex currently is. Ids, never indices — rows and columns move. */
interface Focus {
  rowId: string
  colId: string
}

/** The cell being edited, plus the character that opened the edit (see GridCell#seed). */
interface EditTarget extends Focus {
  seed?: string
}

/**
 * The grid part editor — a semantic `<table role="grid">` with a roving tabindex.
 *
 * NO GRID LIBRARY, deliberately. client/web carries none today, and for a values-only grid the
 * right answer is plain semantic markup: it passes jest-axe (canvas-drawn grids like glide cannot
 * expose a cell to a screen reader at all), it adds no dependency, and virtualisation only starts
 * to matter in the thousands of rows — a bid's pricing grid is tens.
 *
 * It writes STRAIGHT INTO THE CRDT, exactly as RichEditor does for prose: the human edits the
 * ydoc and the change is broadcast; only the AI path goes through REST (`PUT /v1/revisions/:id/
 * grid`). Human edits are captured into the same `Y.Array('history')` the AI writes to, so one
 * log covers both — the grid twin of components/editor/useHistoryCapture.ts.
 */
export function GridEditor({ ydoc, awareness, actor, readOnly = false, revisions = [], releaseSignal = 0 }: GridEditorProps) {
  const { t } = useLang()
  const [grid, setGrid] = useState<GridSnapshot>(() => readGrid(ydoc))
  const [focus, setFocus] = useState<Focus | null>(null)
  const [editingCell, setEditingCell] = useState<EditTarget | null>(null)

  // One deep observer over the whole grid: the columns/rows maps, their order arrays, and every
  // cell record beneath them. Cheaper and far less error-prone than five separate subscriptions
  // that would each have to be re-bound as rows come and go.
  useEffect(() => {
    const refresh = (): void => setGrid(readGrid(ydoc))
    const targets = [
      ydoc.getMap('columns'),
      ydoc.getMap('rows'),
      ydoc.getArray('columnOrder'),
      ydoc.getArray('rowOrder'),
    ]
    for (const target of targets) target.observeDeep(refresh)
    refresh()
    return () => {
      for (const target of targets) target.unobserveDeep(refresh)
    }
  }, [ydoc])

  // Publish which cell this member is on, so other browsers can shade it — the same awareness
  // channel that carries the prose editor's named cursors. It is also the member's human lock
  // (lock conflicts, AB#298): a viewer's cell carries `ro: true` (presence only, never a lock),
  // and an idle member (2 min without input in the grid) publishes null. A hidden tab keeps it.
  const wrapRef = useRef<HTMLDivElement>(null)
  const idle = useIdle(wrapRef, !!awareness && !readOnly)
  useEffect(() => {
    if (!awareness) return
    awareness.setLocalStateField(
      'cell',
      focus && !idle
        ? { rowId: focus.rowId, colId: focus.colId, ...(readOnly ? { ro: true } : {}) }
        : null,
    )
  }, [awareness, focus, idle, readOnly])

  // Release (AB#301): another member's agent is waiting for this member's cell — step off it.
  useEffect(() => {
    if (releaseSignal === 0) return
    setEditingCell(null)
    setFocus(null)
  }, [releaseSignal])

  /**
   * Who is on each cell, by address. Two sources, both display-only: remote awareness (a teammate's
   * caret, live) and the room's cell claims (an agent's declared range, which survives across the
   * seconds it spends generating).
   */
  const busy = useMemo(() => {
    const map = new Map<string, string>()
    for (const rev of revisions) {
      if (!rev.cells) continue
      const who = rev.actorName ?? rev.actor
      for (const rowId of rev.cells.rowIds) {
        for (const colId of rev.cells.colIds) map.set(`${rowId}/${colId}`, who)
      }
    }
    awareness?.getStates().forEach((state, clientId) => {
      if (clientId === awareness.clientID) return
      const s = state as { user?: { name: string }; cell?: { rowId: string; colId: string } | null }
      if (s.cell) map.set(`${s.cell.rowId}/${s.cell.colId}`, s.user?.name ?? '')
    })
    return map
  }, [revisions, awareness])

  /** A structural (unscoped) claim freezes the grid: rows are about to move under the member. */
  const frozenBy = useMemo(() => {
    const live = revisions.find((r) => r.status === 'working' && !r.cells && !r.section)
    return live ? (live.actorName ?? live.actor) : null
  }, [revisions])

  const locked = readOnly || frozenBy !== null

  /** Record a human cell edit into the SAME history the AI writes to. One entry per commit. */
  const logCells = useCallback(
    (cells: CellChange[]): void => {
      if (cells.length === 0) return
      const entry: HistoryEntry = {
        id: crypto.randomUUID(),
        actor,
        kind: 'human',
        cells,
        createdAt: new Date().toISOString(),
      }
      appendHistory(ydoc, entry)
    },
    [actor, ydoc],
  )

  const commit = useCallback(
    (rowId: string, colId: string, raw: string): void => {
      setEditingCell(null)
      const column = grid.columns.find((c) => c.id === colId)
      if (!column || locked) return
      const value = coerceCell(raw, column.type)
      const previous = cellAt(grid, rowId, colId)
      if (formatCell(value) === formatCell(previous)) return
      writeCell(ydoc, rowId, colId, value)
      logCells([
        {
          rowId,
          colId,
          colLabel: column.label,
          ...(rowLabel(grid, rowId) ? { rowLabel: rowLabel(grid, rowId) } : {}),
          before: formatCell(previous),
          after: formatCell(value),
        },
      ])
    },
    [grid, locked, logCells, ydoc],
  )

  /** Move the roving focus by a cell delta, clamped at the edges. */
  const moveFocus = useCallback(
    (from: Focus, dRow: number, dCol: number): void => {
      const r = grid.rows.findIndex((row) => row.id === from.rowId)
      const c = grid.columns.findIndex((col) => col.id === from.colId)
      if (r === -1 || c === -1) return
      const nr = Math.max(0, Math.min(r + dRow, grid.rows.length - 1))
      const nc = Math.max(0, Math.min(c + dCol, grid.columns.length - 1))
      setFocus({ rowId: grid.rows[nr]!.id, colId: grid.columns[nc]!.id })
    },
    [grid],
  )

  function cellKeyDown(e: React.KeyboardEvent, cell: Focus): void {
    const keys: Record<string, [number, number]> = {
      ArrowUp: [-1, 0],
      ArrowDown: [1, 0],
      ArrowLeft: [0, -1],
      ArrowRight: [0, 1],
    }
    const delta = keys[e.key]
    if (delta) {
      e.preventDefault()
      moveFocus(cell, delta[0], delta[1])
      return
    }
    if (e.key === 'Home' || e.key === 'End') {
      e.preventDefault()
      const colId = grid.columns[e.key === 'Home' ? 0 : grid.columns.length - 1]?.id
      if (colId) setFocus({ rowId: cell.rowId, colId })
      return
    }
    if (locked) return
    if (e.key === 'Enter' || e.key === 'F2') {
      e.preventDefault()
      setEditingCell(cell)
      return
    }
    if (e.key === 'Delete' || e.key === 'Backspace') {
      e.preventDefault()
      commit(cell.rowId, cell.colId, '')
      return
    }
    // Typing a printable character starts an edit and REPLACES the cell with that character —
    // the spreadsheet reflex. The keystroke rides in as state, never replayed onto the DOM
    // afterwards: a synthetic `input` event on a controlled React field is a race.
    // Modifier chords are left alone so Ctrl/Cmd shortcuts still reach the browser.
    if (e.key.length === 1 && !e.ctrlKey && !e.metaKey && !e.altKey) {
      e.preventDefault()
      setEditingCell({ ...cell, seed: e.key })
    }
  }

  // The roving tabindex target: the focused cell, else the first cell, so the grid always has
  // exactly one Tab stop even before anyone has clicked into it.
  const roving: Focus | null =
    focus ?? (grid.rows[0] && grid.columns[0] ? { rowId: grid.rows[0].id, colId: grid.columns[0].id } : null)

  function addRow(): void {
    const id = insertRow(ydoc)
    setFocus(grid.columns[0] ? { rowId: id, colId: grid.columns[0].id } : null)
  }

  function addColumn(): void {
    insertColumn(ydoc, t('grid_new_col'))
  }

  function removeRow(rowId: string): void {
    deleteRow(ydoc, rowId)
    if (focus?.rowId === rowId) setFocus(null)
  }

  function removeColumn(colId: string): void {
    deleteColumn(ydoc, colId)
    if (focus?.colId === colId) setFocus(null)
  }

  if (grid.columns.length === 0) {
    return (
      <div className="gridwrap">
        <p className="lede">{t('grid_empty')}</p>
        {!locked && (
          <button className="fmtbtn" onClick={addColumn}>
            + {t('grid_add_col')}
          </button>
        )}
      </div>
    )
  }

  return (
    <div className="gridwrap" ref={wrapRef}>
      {frozenBy && (
        <p className="grid-frozen" role="note">
          <span aria-hidden="true">◆</span> {t('grid_frozen').replace('{name}', () => frozenBy)}
        </p>
      )}

      <div className="gridscroll">
        <table className="grid" role="grid" aria-label={t('grid_label')}>
          <thead>
            <tr>
              {/* The row-number gutter. `role="columnheader"` with no name of its own would be read
                  as an empty header on every row, so it carries one. */}
              <th scope="col" className="ghead grow-num">
                <span className="visually-hidden">{t('grid_row_no')}</span>
              </th>
              {grid.columns.map((col) => (
                <th
                  key={col.id}
                  scope="col"
                  className="ghead"
                  style={col.width ? { width: `${col.width}px` } : undefined}
                >
                  <div className="ghead-in">
                    <input
                      className="ghead-label"
                      value={col.label}
                      aria-label={`${t('grid_col_name')} — ${col.label}`}
                      disabled={locked}
                      onChange={(e) => updateColumn(ydoc, col.id, { label: e.target.value })}
                    />
                    {/* The column TYPE is the whole reason this is not a pipe-table: it is what
                        makes the xlsx export write a real number with a currency format, instead
                        of guessing from the header name. */}
                    <select
                      className="ghead-type"
                      value={col.type}
                      aria-label={`${t('grid_col_type')} — ${col.label}`}
                      disabled={locked}
                      onChange={(e) => updateColumn(ydoc, col.id, { type: e.target.value as GridCellType })}
                    >
                      {GRID_CELL_TYPES.map((type) => (
                        <option key={type} value={type}>
                          {t(`grid_type_${type}` as 'grid_type_text')}
                        </option>
                      ))}
                    </select>
                    {!locked && (
                      <button
                        className="ghead-del"
                        aria-label={`${t('grid_del_col')} — ${col.label}`}
                        onClick={() => removeColumn(col.id)}
                      >
                        ×
                      </button>
                    )}
                  </div>
                </th>
              ))}
              <th scope="col" className="ghead grow-act">
                <span className="visually-hidden">{t('grid_row_actions')}</span>
              </th>
            </tr>
          </thead>
          <tbody>
            {grid.rows.map((row, r) => (
              <tr key={row.id}>
                <th scope="row" className="grow-num">
                  {r + 1}
                </th>
                {grid.columns.map((col) => {
                  const cell = { rowId: row.id, colId: col.id }
                  const busyBy = busy.get(`${row.id}/${col.id}`)
                  const editing = editingCell?.rowId === row.id && editingCell?.colId === col.id
                  return (
                    <GridCell
                      key={col.id}
                      value={row.cells[col.id] ?? null}
                      type={col.type}
                      // Read as "Unit price, 3" — the column name plus the row's ordinal, which is
                      // what a member sees on screen. Never the ids: they are addresses, not names.
                      label={`${col.label}, ${r + 1}`}
                      focused={roving?.rowId === row.id && roving?.colId === col.id}
                      editing={editing}
                      {...(editing && editingCell?.seed !== undefined ? { seed: editingCell.seed } : {})}
                      readOnly={locked}
                      {...(busyBy ? { busyBy } : {})}
                      onFocus={() => setFocus(cell)}
                      onStartEdit={() => setEditingCell(cell)}
                      onCommit={(raw) => commit(row.id, col.id, raw)}
                      onCancelEdit={() => setEditingCell(null)}
                      onKeyDown={(e) => cellKeyDown(e, cell)}
                    />
                  )
                })}
                <td className="grow-act">
                  {!locked && (
                    <span className="growbtns">
                      <button
                        className="growbtn"
                        aria-label={`${t('grid_row_up')} ${r + 1}`}
                        disabled={r === 0}
                        onClick={() => moveRow(ydoc, row.id, r - 1)}
                      >
                        ↑
                      </button>
                      <button
                        className="growbtn"
                        aria-label={`${t('grid_row_down')} ${r + 1}`}
                        disabled={r === grid.rows.length - 1}
                        onClick={() => moveRow(ydoc, row.id, r + 1)}
                      >
                        ↓
                      </button>
                      <button
                        className="growbtn danger"
                        aria-label={`${t('grid_del_row')} ${r + 1}`}
                        onClick={() => removeRow(row.id)}
                      >
                        ×
                      </button>
                    </span>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {!locked && (
        <div className="gridbar">
          <button className="fmtbtn" onClick={addRow}>
            + {t('grid_add_row')}
          </button>
          <button className="fmtbtn" onClick={addColumn}>
            + {t('grid_add_col')}
          </button>
          <span className="grid-count">
            {grid.rows.length} × {grid.columns.length}
          </span>
        </div>
      )}
    </div>
  )
}
